import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ExternalLink, File } from 'lucide-react';
import type { MalwareIntelligence as MalwareData } from '@/lib/types/malware';

interface MalwareIntelligenceProps {
  data: MalwareData[];
}

export function MalwareIntelligence({ data }: MalwareIntelligenceProps) {
  console.log('MalwareIntelligence component received data:', data);

  if (!data.length) {
    console.log('No malware data available, returning null');
    return null;
  }

  const malware = data[0]; // Use the first result
  console.log('Using first malware result:', malware);

  function formatDate(dateStr: string | null) {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString();
    } catch {
      return dateStr;
    }
  }

  function getVerdictColor(verdict: string | undefined | null) {
    if (!verdict) return 'secondary';
    
    verdict = verdict.toLowerCase();
    if (verdict.includes('malicious')) return 'destructive';
    if (verdict.includes('suspicious')) return 'warning';
    if (verdict.includes('clean') || verdict === 'ok') return 'default';
    return 'secondary';
  }

  function formatSize(size: number) {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  return (
    <div className="mt-6 space-y-4">
      <h2 className="text-2xl font-ibm-condensed">Malware Intelligence</h2>
      
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="text-xl flex items-center gap-2">
              <File className="h-5 w-5 text-primary" />
              {malware.file_name}
            </CardTitle>
            <Badge variant="destructive">{malware.signature}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="overview">
            <TabsList className="mb-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="hashes">Hashes</TabsTrigger>
              <TabsTrigger value="vendor">Vendor Analysis</TabsTrigger>
              {malware.vendor_intel?.Triage?.signatures && (
                <TabsTrigger value="signatures">Signatures</TabsTrigger>
              )}
            </TabsList>
            
            <TabsContent value="overview">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-start gap-4">
                    <span className="text-sm font-medium text-muted-foreground w-32">File Type:</span>
                    <span className="text-sm flex-1">{malware.file_type_mime} ({malware.file_type})</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-sm font-medium text-muted-foreground w-32">Size:</span>
                    <span className="text-sm flex-1">{formatSize(malware.file_size)}</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-sm font-medium text-muted-foreground w-32">First Seen:</span>
                    <span className="text-sm flex-1">{formatDate(malware.first_seen)}</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-sm font-medium text-muted-foreground w-32">Last Seen:</span>
                    <span className="text-sm flex-1">{formatDate(malware.last_seen)}</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-sm font-medium text-muted-foreground w-32">Origin Country:</span>
                    <span className="text-sm flex-1">{malware.origin_country}</span>
                  </div>
                  {malware.delivery_method && (
                    <div className="flex items-start gap-4">
                      <span className="text-sm font-medium text-muted-foreground w-32">Delivery Method:</span>
                      <span className="text-sm flex-1">{malware.delivery_method.replace('_', ' ')}</span>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-start gap-4">
                    <span className="text-sm font-medium text-muted-foreground w-32">Downloads:</span>
                    <span className="text-sm flex-1">{malware.intelligence.downloads}</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-sm font-medium text-muted-foreground w-32">Uploads:</span>
                    <span className="text-sm flex-1">{malware.intelligence.uploads}</span>
                  </div>
                  {malware.comment && (
                    <div className="flex items-start gap-4 pt-2">
                      <span className="text-sm font-medium text-muted-foreground w-32">Comment:</span>
                      <p className="text-sm break-all whitespace-pre-line flex-1">{malware.comment}</p>
                    </div>
                  )}
                  {malware.tags && malware.tags.length > 0 && (
                    <div className="pt-2">
                      <span className="text-sm font-medium text-muted-foreground">Tags:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {malware.tags.map(tag => (
                          <Badge key={tag} variant="secondary">{tag}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {malware.file_information && Array.isArray(malware.file_information) && malware.file_information.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-md font-medium mb-2">File Information</h3>
                  <div className="space-y-2">
                    {malware.file_information.map((info, index) => (
                      <div key={index} className="text-sm">
                        <span className="font-medium text-muted-foreground">{info.context}: </span>
                        <a 
                          href={info.value} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline flex items-center gap-1 inline-flex"
                        >
                          {info.value}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {malware.trid && Array.isArray(malware.trid) && malware.trid.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-md font-medium mb-2">File Type Identification</h3>
                  <div className="space-y-1">
                    {malware.trid.map((tridEntry, index) => (
                      <div key={index} className="text-sm">
                        {tridEntry}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="hashes">
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-muted-foreground">SHA-256:</span>
                  <p className="text-sm font-mono mt-1 break-all">{malware.sha256_hash}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-muted-foreground">SHA3-384:</span>
                  <p className="text-sm font-mono mt-1 break-all">{malware.sha3_384_hash}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-muted-foreground">SHA-1:</span>
                  <p className="text-sm font-mono mt-1 break-all">{malware.sha1_hash}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-muted-foreground">MD5:</span>
                  <p className="text-sm font-mono mt-1 break-all">{malware.md5_hash}</p>
                </div>

                {malware.imphash && (
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Import Hash:</span>
                    <p className="text-sm font-mono mt-1 break-all">{malware.imphash}</p>
                  </div>
                )}
                
                {malware.tlsh && (
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">TLSH:</span>
                    <p className="text-sm font-mono mt-1 break-all">{malware.tlsh}</p>
                  </div>
                )}
                
                {malware.ssdeep && (
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">SSDEEP:</span>
                    <p className="text-sm font-mono mt-1 break-all">{malware.ssdeep}</p>
                  </div>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="vendor">
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-4">
                  {Object.entries(malware.vendor_intel || {}).map(([vendor, intel]) => {
                    if (!intel) return null;
                    
                    // Handle arrays (like Spamhaus_HBL)
                    if (Array.isArray(intel)) {
                      return (
                        <div key={vendor} className="border rounded-md p-3">
                          <div className="flex items-center justify-between">
                            <h3 className="text-md font-medium">{vendor.replace(/_/g, ' ')}</h3>
                            {intel[0]?.detection && (
                              <Badge variant={getVerdictColor(intel[0].detection)}>
                                {intel[0].detection}
                              </Badge>
                            )}
                          </div>
                          {intel[0]?.link && (
                            <a 
                              href={intel[0].link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:underline flex items-center gap-1 mt-2"
                            >
                              <span>View Report</span>
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                          {intel[0]?.analysis_url && (
                            <a 
                              href={intel[0].analysis_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:underline flex items-center gap-1 mt-2"
                            >
                              <span>View Analysis</span>
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                          {intel[0]?.tags && intel[0].tags.length > 0 && (
                            <div className="mt-3">
                              <span className="text-sm text-muted-foreground mb-1 block">Tags:</span>
                              <div className="flex flex-wrap gap-1">
                                {intel[0].tags.map((tag) => (
                                  <Badge key={tag} variant="outline" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    // Handle standard vendor intel objects
                    return (
                      <div key={vendor} className="border rounded-md p-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-md font-medium">{vendor.replace(/_/g, ' ')}</h3>
                          {(intel.verdict || intel.detection) && (
                            <Badge variant={getVerdictColor(intel.verdict || intel.detection)}>
                              {intel.verdict || intel.detection}
                            </Badge>
                          )}
                        </div>
                        <div className="mt-2 space-y-1 text-sm">
                          {intel.score && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Score:</span>
                              <span className="font-medium">{intel.score}</span>
                            </div>
                          )}
                          {intel.maliciousness && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Maliciousness:</span>
                              <span className="font-medium">{intel.maliciousness}/100</span>
                            </div>
                          )}
                          {intel.threat_name && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Threat Name:</span>
                              <span className="font-medium">{intel.threat_name}</span>
                            </div>
                          )}
                          {intel.malware_family && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Malware Family:</span>
                              <span className="font-medium">{intel.malware_family}</span>
                            </div>
                          )}
                          {intel.first_seen && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">First Seen:</span>
                              <span>{formatDate(intel.first_seen)}</span>
                            </div>
                          )}
                          {intel.scanner_count && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Scanner Detection:</span>
                              <span>{intel.scanner_match}/{intel.scanner_count} ({intel.scanner_percent}%)</span>
                            </div>
                          )}
                        </div>
                        {(intel.link || intel.report_link || intel.analysis_url) && (
                          <a 
                            href={intel.link || intel.report_link || intel.analysis_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline flex items-center gap-1 mt-2"
                          >
                            <span>View Report</span>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        {intel.tags && intel.tags.length > 0 && (
                          <div className="mt-3">
                            <span className="text-sm text-muted-foreground mb-1 block">Tags:</span>
                            <div className="flex flex-wrap gap-1">
                              {intel.tags.map((tag) => (
                                <Badge key={tag} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {intel.malware_config && intel.malware_config.length > 0 && (
                          <div className="mt-3">
                            <span className="text-sm text-muted-foreground mb-1 block">Malware Configuration:</span>
                            <div className="space-y-2">
                              {intel.malware_config.map((config, idx) => (
                                <div key={idx} className="bg-muted p-2 rounded-sm text-xs">
                                  <div><span className="font-medium">Family:</span> {config.family}</div>
                                  <div><span className="font-medium">C2:</span> {config.c2}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {intel.behaviour && intel.behaviour.length > 0 && (
                          <div className="mt-3">
                            <span className="text-sm text-muted-foreground mb-1 block">Behavior:</span>
                            <div className="space-y-1">
                              {intel.behaviour.map((behavior, idx) => (
                                <div key={idx} className="flex items-center justify-between">
                                  <span>{behavior.rule}</span>
                                  <Badge 
                                    variant={
                                      behavior.threat_level === "malicious" ? "destructive" :
                                      behavior.threat_level === "suspicious" ? "warning" :
                                      "secondary"
                                    }
                                    className="text-xs"
                                  >
                                    {behavior.threat_level}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </TabsContent>
            
            {malware.vendor_intel?.Triage?.signatures && (
              <TabsContent value="signatures">
                <ScrollArea className="h-[400px] pr-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Signature</TableHead>
                        <TableHead className="text-right w-20">Score</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {malware.vendor_intel.Triage.signatures
                        .sort((a, b) => {
                          const scoreA = a.score ? parseInt(a.score) : 0;
                          const scoreB = b.score ? parseInt(b.score) : 0;
                          return scoreB - scoreA;
                        })
                        .map((signature, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{signature.signature}</TableCell>
                            <TableCell className="text-right">
                              {signature.score ? signature.score : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}